import React, { useEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import Select from "react-select";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import { v4 as uuidv4 } from "uuid";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

import backend from "../../backend";
import { formatInt, formatPrice } from "../../utils";

import CheckBack from "../../components/CheckBack";
import WaitAsync from "../../components/WaitAsync";
import PriceInput from "../../components/PriceInput";
import PiecePrice from "../../components/PiecePrice";
import ButtonGroupFilter from "../../components/ButtonGroupFilter";
import LoadingOverlay from "../../components/LoadingOverlay";
import IntegerInput from "../../components/IntegerInput";
import FileInput from "../../components/FileInput";
import PlanoYCotas from "./PlanoYCotas";

// 01-sep (Maxi, simulacro): borrador automatico de Nueva Pieza — un F5 por error
// no puede botar la ficha (menos aun el borrador que armo la IA). Mismo patron
// del cotizador: sessionStorage, 1 hora, restaurar al volver, descartar explicito.
const PIECE_DRAFT_KEY = "binimeliz:pieceForm:draft:v1";
const PIECE_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 h — localStorage: sobrevive cerrar el navegador (01-sep)
const loadPieceDraft = () => {
  try {
    const raw = localStorage.getItem(PIECE_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d?.savedAt || Date.now() - d.savedAt > PIECE_DRAFT_MAX_AGE_MS) {
      localStorage.removeItem(PIECE_DRAFT_KEY);
      return null;
    }
    return d;
  } catch { return null; }
};
const clearPieceDraft = () => { try { localStorage.removeItem(PIECE_DRAFT_KEY); } catch {} };

const PieceForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  // 01-sep (friccion #6): _id tambien por query — permite aterrizar en EDICION
  // recien creada la pieza (recarga dura) para cargar plano maestro y cotas al tiro.
  const _id = location.state?._id || new URLSearchParams(location.search).get("editar");
  const avisoPlanoRecienCreada = new URLSearchParams(location.search).get("plano") === "1";
  const fromQuotation = location.state?.fromQuotation;
  const quotationDraftId = location.state?.quotationDraftId; // para reanudar ESA cotizacion al volver

  const loadingOverlayRef = useRef();

  const getPiece = async () => {
    try {
      const piece = (await backend.get(`piece/${_id}`)).data;
      setName(piece.name);
      setSku(piece.sku);
      setOriginalSku(piece.sku || "");
      setDetails(piece.details);
      if (piece.tipoTrabajo) setTipoTrabajo(piece.tipoTrabajo);
      setMaterial(piece.material);
      setGeometry(piece.geometry);
      setMaterialPrice(formatPrice(piece.materialPrice) || "");
      setBlueprint(piece.blueprint);
      setFirstBlueprint(piece.blueprint);
      setMinStock(piece.minStock || "");
      setPrice(formatPrice(piece.price) || "");
      setPriceClient(formatPrice(piece.priceClient) || "");
      setRequiereDosier(!!piece.requiereDosier);
      setEsImportada(!!piece.esImportada);
      setMaterialRef(piece.materialRef?._id || piece.materialRef || "");
      setConsumoPorUnidad(String(piece.consumoPorUnidad ?? 1));
      setComponents(piece.components || []);

      // Fill missing constants
      const currentConstants = (await backend.get("constants")).data;

      // Piezas viejas o sincronizadas desde /produccion pueden no tener
      // `constants` asignado. Usar las globales como base para que el form
      // no rompa al acceder a piece.constants.prices.
      if (!piece.constants) {
        piece.constants = {
          ...currentConstants,
          prices: [...(currentConstants.prices || [])],
        };
      } else {
        if (!Array.isArray(piece.constants.prices)) piece.constants.prices = [];
        for (const price of currentConstants.prices) {
          if (!piece.constants.prices.find(v => v.machineType === price.machineType)) {
            piece.constants.prices.push(price);
          }
        }
      }

      setConstants(piece.constants);
      const newProcesses = piece.processes;
      for (const process of newProcesses) {
        if (process.external) {
          process.machineType = "";
          process.machines = [];
        } else {
          process.price = "";
          process.machineType.value = process.machineType._id;
          process.machineType.label = process.machineType.type;
          for (const machine of process.machines) {
            machine.value = machine._id;
            machine.label = machine.name;
          }
        }
      }
      setProcesses(newProcesses);
    } catch (err) {
      console.log(err);
    }
  };

  const [validated, setValidated] = useState(false);
  const [error, setError] = useState();
  const [fileError, setFileError] = useState();
  const [skuCheck, setSkuCheck] = useState(null);
  // null = sin verificar | 'checking' | { ok: true } | { ok: false, existing: {name} }
  const [originalSku, setOriginalSku] = useState("");

  const getNewProcess = () => ({
    _id: uuidv4(),
    fakeId: true,
    machineType: "",
    machines: [],
    duration: "",
    name: "",
    external: false,
    price: "",
    priceSelected: "price",
  });

  const [name, setName] = useState("");
  // 01-sep: asistente de planos — la IA propone el borrador, la persona corrige.
  const [tipoTrabajo, setTipoTrabajo] = useState("ref");
  const [pieceDraftRestaurado, setPieceDraftRestaurado] = useState(false);
  const [draftBtnMsg, setDraftBtnMsg] = useState(""); // confirmacion del boton Guardar borrador
  const [iaCargando, setIaCargando] = useState(false);
  const [iaBorrador, setIaBorrador] = useState(null);
  const [iaError, setIaError] = useState("");
  const [details, setDetails] = useState("");
  const [sku, setSku] = useState(() => {
    // D-T51b — prefill SKU desde alerta sku_no_registrado en /pulido
    const sp = new URLSearchParams(location.search);
    return sp.get("suggestedSku") || "";
  });

  // D-T51b — focus en campo SKU si vinimos de "sku_no_registrado"
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (!_id && sp.get("suggestedSku")) {
      setTimeout(() => {
        const skuInput = document.querySelector('input[name="sku"], input[placeholder*="SKU"], input[placeholder*="sku"]');
        if (skuInput) {
          try { skuInput.focus(); } catch {}
        }
      }, 200);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [material, setMaterial] = useState("");
  const [geometry, setGeometry] = useState("");
  const [materialPrice, setMaterialPrice] = useState("");
  const [blueprint, setBlueprint] = useState("");
  // Procesos arranca vacío. Antes había una fila default que disparaba la
  // validación (name+duration+machineType requeridos) y bloqueaba a usuarios
  // que solo querían cargar una cotización existente con precio ya conocido.
  // Si la pieza necesita calculadora, se hace click en "+ Agregar proceso".
  const [processes, setProcesses] = useState([]);
  const [price, setPrice] = useState("");
  const [minStock, setMinStock] = useState("");
  const [priceClient, setPriceClient] = useState("");
  const [requiereDosier, setRequiereDosier] = useState(false);
  const [esImportada, setEsImportada] = useState(false);
  const [materialRef, setMaterialRef] = useState("");
  const [consumoPorUnidad, setConsumoPorUnidad] = useState("1");
  const [components, setComponents] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);

  const blueprintRef = useRef();

  const [machineTypes, setMachineTypes] = useState([]);
  const [constants, setConstants] = useState();
  const [drive, setDrive] = useState();

  const [firstBlueprint, setFirstBlueprint] = useState();

  const getMachineTypes = async () => {
    try {
      const machineTypesData = (await backend.get("machineType")).data;
      setMachineTypes(
        machineTypesData.map(v => ({
          ...v,
          value: v._id,
          label: v.type,
        }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  const getConstants = async () => {
    try {
      setConstants((await backend.get("constants")).data);
    } catch (err) {
      console.log(err);
    }
  };

  const getDrive = async () => {
    try {
      setDrive((await backend.get("data/drive")).data);
    } catch (err) {
      console.log(err);
    }
  };

  const getData = async () => {
    backend.get("rawMaterial").then(r => setRawMaterials(r.data || [])).catch(() => setRawMaterials([]));
    await getDrive();
    await getMachineTypes();
    // Modo edit cuando: la ruta dice "edit", o llegamos a "new" con _id en
    // state (botón "Editar procesos" desde modal P3 en /pieces).
    if (_id || location.pathname.includes("edit")) {
      await getPiece();
    } else {
      await getConstants();
    }
  };

  const handleBlueprintUpload = async e => {
    setFileError("");
    try {
      const newFile = e.target.files[0];
      if (newFile.name.split(".").slice(-1)[0] !== "pdf") {
        setFileError("Debes subir un pdf");
      }
      const formData = new FormData();
      formData.append("blueprint", newFile);
      const newBlueprint = (await backend.post("piece/blueprint", formData))
        .data;
      if (firstBlueprint?.id && blueprint.id !== firstBlueprint.id) {
        await handleBlueprintRemove();
      }
      setBlueprint(newBlueprint);
    } catch (err) {
      console.log(err);
      setFileError(err.message);
    }
  };

  const handleBlueprintRemove = async () => {
    try {
      await backend.delete("piece/blueprint", { data: { blueprint } });
      setBlueprint("");
    } catch (err) {
      console.log(err);
    }
  };

  const handleProcessPriceSelectedChanged = (i, priceSelected) => {
    const newProcesses = JSON.parse(JSON.stringify(processes));
    newProcesses[i].priceSelected = priceSelected;
    setProcesses(newProcesses);
  };

  const handleProfitChanged = profit => {
    setConstants(p => ({ ...p, profit }));
  };

  const handleConstantsUpdate = async () => {
    try {
      setConstants((await backend.get("constants")).data);
    } catch (err) {
      console.log(err);
    }
  };

  const verificarSKU = async (skuValue) => {
    const skuTrim = skuValue?.trim();
    // En edición, si el SKU no cambió no hay nada que verificar
    if (!skuTrim || skuTrim.length < 4) { setSkuCheck(null); return; }
    if (_id && skuTrim === originalSku) { setSkuCheck(null); return; }

    setSkuCheck("checking");
    try {
      const res = await backend.get(`piece/verificar-sku?sku=${encodeURIComponent(skuTrim)}`);
      if (res.data.existe) {
        setSkuCheck({ ok: false, existing: res.data.pieza });
      } else {
        setSkuCheck({ ok: true });
      }
    } catch (e) {
      setSkuCheck(null);
    }
  };

  const cancel = () => {
    if (!firstBlueprint?.id || firstBlueprint.id !== blueprint?.id) {
      handleBlueprintRemove();
    }

    if (fromQuotation) {
      navigate("/quotations/new", { replace: true, state: { resumeDraftId: quotationDraftId } });
    } else {
      navigationType === "POP"
        ? navigate("/pieces", { replace: true })
        : navigate(-1);
    }
  };

  // Restaurar borrador (solo Nueva Pieza). Corre una vez al montar.
  useEffect(() => {
    if (_id) return;
    const d = loadPieceDraft();
    if (!d) return;
    setPieceDraftRestaurado(true);
    if (d.name) setName(d.name);
    if (d.sku) setSku(d.sku);
    if (d.details) setDetails(d.details);
    if (d.minStock) setMinStock(d.minStock);
    if (d.material) setMaterial(d.material);
    if (d.geometry) setGeometry(d.geometry);
    if (d.materialPrice) setMaterialPrice(d.materialPrice);
    if (d.price) setPrice(d.price);
    if (d.priceClient) setPriceClient(d.priceClient);
    if (d.tipoTrabajo) setTipoTrabajo(d.tipoTrabajo);
    if (d.requiereDosier != null) setRequiereDosier(d.requiereDosier);
    if (d.esImportada != null) setEsImportada(d.esImportada);
    if (d.materialRef) setMaterialRef(d.materialRef);
    if (d.consumoPorUnidad) setConsumoPorUnidad(d.consumoPorUnidad);
    if (Array.isArray(d.processes) && d.processes.length) setProcesses(d.processes);
    if (d.iaBorrador) setIaBorrador(d.iaBorrador);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autoguardado del borrador en cada cambio (solo Nueva Pieza).
  useEffect(() => {
    if (_id) return;
    try {
      localStorage.setItem(PIECE_DRAFT_KEY, JSON.stringify({
        savedAt: Date.now(),
        name, sku, details, minStock, material, geometry, materialPrice,
        price, priceClient, tipoTrabajo, requiereDosier, esImportada,
        materialRef, consumoPorUnidad, processes, iaBorrador,
      }));
    } catch {}
  }, [_id, name, sku, details, minStock, material, geometry, materialPrice,
      price, priceClient, tipoTrabajo, requiereDosier, esImportada,
      materialRef, consumoPorUnidad, processes, iaBorrador]);

  // Guardado EXPLICITO (boton "por si acaso"): mismo contenido que el autoguardado,
  // pero con confirmacion visible para la tranquilidad de quien opera.
  const guardarBorradorAhora = () => {
    try {
      localStorage.setItem(PIECE_DRAFT_KEY, JSON.stringify({
        savedAt: Date.now(),
        name, sku, details, minStock, material, geometry, materialPrice,
        price, priceClient, tipoTrabajo, requiereDosier, esImportada,
        materialRef, consumoPorUnidad, processes, iaBorrador,
      }));
      const hh = new Date();
      setDraftBtnMsg("✔ Borrador guardado a las " + String(hh.getHours()).padStart(2, "0") + ":" + String(hh.getMinutes()).padStart(2, "0") + " — dura 24 h, puede cerrar tranquilo");
      setTimeout(() => setDraftBtnMsg(""), 6000);
    } catch {
      setDraftBtnMsg("⚠ No se pudo guardar el borrador");
      setTimeout(() => setDraftBtnMsg(""), 6000);
    }
  };

  const submit = async () => {
    // delete original blueprint if different from current
    // this seems to be happening already
    // if (firstBlueprint?.id && firstBlueprint.id !== blueprint?.id) {
    //   await backend.delete("piece/blueprint", {
    //     data: { blueprint: firstBlueprint },
    //   });
    // }

    loadingOverlayRef.current?.show();
    setValidated(false);
    setError(undefined);

    try {
      if (skuCheck?.ok === false) {
        throw new Error(`El SKU "${sku.trim()}" ya existe en el catálogo como "${skuCheck.existing?.name}". Corrígelo antes de guardar.`);
      }
      if (!sku || !name || !price || fileError) {
        throw new Error("Faltan campos por completar");
      }
      const processesData = JSON.parse(JSON.stringify(processes));
      for (let i = 0; i < processesData.length; ++i) {
        if (processesData[i].fakeId) {
          delete processesData[i]._id;
        }
        if (processesData[i].external) {
          delete processesData[i].machineType;
          delete processesData[i].machines;
          processesData[i].price = +formatInt(processesData[i].price);
        } else {
          delete processesData[i].price;
          processesData[i].machineType = processesData[i].machineType.value;
        }
      }

      for (const process of processes) {
        if (!process.name || process.duration === "") {
          throw new Error("Faltan campos por completar");
        }

        if (process.external) {
          if (!process.price) {
            throw new Error("Faltan campos por completar");
          }
        } else {
          if (!process.machineType) {
            throw new Error("Falta elegir el tipo de máquina");
          }
        }
      }

      // 02-sep (Maxi): elegir la maquina EXACTA es pega de produccion (Gantt).
      // Si quien cotiza no marco ninguna, dejamos TODAS las maquinas capaces de
      // ese tipo como candidatas y produccion decide al programar la OT.
      for (const pd of processesData) {
        if (!pd.external && (!pd.machines || pd.machines.length === 0)) {
          const capaces = (await backend.get(`machine/type/${pd.machineType}`)).data;
          if (!capaces.length) {
            throw new Error(`No hay máquinas registradas del tipo requerido para "${pd.name}" — regístrela primero en Máquinas`);
          }
          pd.machines = capaces.map(v => v._id);
        }
      }

      if (_id) {
        const editPiece = (
          await backend.patch("piece", {
            _id,
            name,
            details,
            sku,
            tipoTrabajo,
            material,
            geometry,
            materialPrice: materialPrice ? +formatInt(materialPrice) : "",
            blueprint,
            price: +formatInt(price),
            priceClient: priceClient ? +formatInt(priceClient) : 0,
            requiereDosier,
            esImportada,
            materialRef: materialRef || null,
            consumoPorUnidad: Number(consumoPorUnidad) || 1,
            processes: processesData,
            minStock,
            constants,
          })
        ).data;
        navigate("/pieces", { replace: true, state: { edit: editPiece } });
      } else {
        const newPiece = (
          await backend.post("piece", {
            name,
            details,
            sku,
            tipoTrabajo,
            material,
            geometry,
            materialPrice: materialPrice ? +formatInt(materialPrice) : "",
            blueprint,
            price: +formatInt(price),
            priceClient: priceClient ? +formatInt(priceClient) : 0,
            requiereDosier,
            esImportada,
            materialRef: materialRef || undefined,
            consumoPorUnidad: Number(consumoPorUnidad) || 1,
            processes: processesData,
            status: "active",
            minStock,
            constants,
          })
        ).data;

        clearPieceDraft();
        if (fromQuotation) {
          navigate("/quotations/new", { replace: true, state: { newPiece, resumeDraftId: quotationDraftId } });
        } else {
          // Friccion #6 del simulacro: recien creada, la pieza abre en EDICION para
          // cargar el plano maestro y las cotas en el mismo acto (antes habia que
          // volver a buscarla en el catalogo).
          window.location.assign("/pieces/new?editar=" + newPiece._id + "&plano=1");
        }
      }
    } catch (err) {
      console.log(err);
      const backendMsg = err?.response?.data || "";
      if (backendMsg === "Sku exists" || String(backendMsg).includes("exists")) {
        setSkuCheck({ ok: false, existing: { name: "ya registrada en catálogo" } });
        setError("Este SKU ya existe en el catálogo. Verifica el código e inténtalo nuevamente.");
      } else {
        setError(err.message || String(backendMsg));
      }
    }
    setValidated(true);
    loadingOverlayRef.current?.hide();
  };
  return (
    <CheckBack to="/pieces" disabled={location.pathname.includes("new")}>
      <WaitAsync func={getData}>
        <div style={{
          display:"block", padding:"clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background:"#1a1a1a",
          minHeight:"100%", color:"#ddd", fontFamily:"Arial,sans-serif",
          fontSize:13, textAlign:"left", boxSizing:"border-box", width:"100%",
        }}>
          {/* Header */}
          <div style={{ display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
            <div>
              <div style={{ fontSize:16, fontWeight:700, color:"#fff" }}>
                {_id ? `Editar pieza — ${sku}` : "Nueva Pieza"}
              </div>
              <div style={{ fontSize:11, color: "#999", marginTop:2 }}>Catálogo Binimeliz</div>
            </div>
            <button onClick={cancel} style={{
              background:"transparent", color: "#cbd5e1", border:"1px solid #444",
              borderRadius:6, padding:"6px 14px", fontSize:12, cursor:"pointer",
            }}>← Volver</button>
          </div>

          {/* 01-sep — Asistente experto en planos: borrador completo, usted corrige */}

          <div style={{ background: "#131a26", border: "1px solid #1d3a5f", borderRadius: 12, padding: 14, marginBottom: 14 }}>

            <div style={{ fontSize: 12, fontWeight: 700, color: "#3498db", marginBottom: 6 }}>🤖 Asistente experto en planos</div>
              {avisoPlanoRecienCreada && (
                <div style={{ fontSize: 11, color: "#2ecc71", marginBottom: 6, fontWeight: 700 }}>
                  ✅ Pieza creada. Ahora puede cargar el PLANO MAESTRO y las cotas críticas aquí abajo (sección Plano y cotas) — o volver al catálogo.
                </div>
              )}
              {pieceDraftRestaurado && (
                <div style={{ fontSize: 11, color: "#f39c12", marginBottom: 6 }}>
                  💾 Se restauró un borrador de esta pieza (se guarda solo, dura 1 hora). {" "}
                  <button type="button" onClick={() => { clearPieceDraft(); window.location.reload(); }}
                    style={{ background: "none", border: "none", color: "#e74c3c", cursor: "pointer", fontSize: 11, textDecoration: "underline", padding: 0 }}>
                    Descartar y partir de cero
                  </button>
                </div>
              )}

            <div style={{ fontSize: 11, color: "#8a9bb5", marginBottom: 8 }}>Suba el plano (PDF o foto) y le propongo el borrador completo: nombre, código, material, geometría de partida, ruta de procesos y precio referencial. <b>Usted corrige y guarda.</b></div>

            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" disabled={iaCargando}

              onChange={async e => {

                const file = e.target.files && e.target.files[0];

                if (!file) return;

                setIaCargando(true); setIaError(""); setIaBorrador(null);

                try {

                  const fd = new FormData(); fd.append("plano", file);

                  const res = await backend.post("piece/asistente-plano", fd, { headers: { "Content-Type": "multipart/form-data" } });

                  const b = res.data || {};

                  setIaBorrador(b);

                  if (b.nombre)    setName(b.nombre);

                  if (b.codigo)    setSku(b.codigo);

                  if (b.material)  setMaterial(b.material);

                  if (b.geometria) setGeometry(b.geometria);

                  // 25-sep-2026: material en bruto calculado con la regla del taller
                  // (backend/docs/REGLAS_MATERIAL_BRUTO.md): barra del catálogo que sirve
                  // y consumo por unidad (fracción de tocho). Sigue siendo borrador.
                  if (b.materialSugerido && b.materialSugerido._id) setMaterialRef(String(b.materialSugerido._id));
                  if (b.materialBruto && b.materialBruto.aplica && b.materialBruto.consumoPorUnidad) setConsumoPorUnidad(String(b.materialBruto.consumoPorUnidad));

                  if (b.detalles)  setDetails(b.detalles);
        if (b.tipoTrabajo) setTipoTrabajo(b.tipoTrabajo);
        // Pipeline: el borrador arma las filas de PROCESOS de verdad (maquina por
        // tipo, minutos por unidad). La persona elige la maquina especifica y corrige.
        if (Array.isArray(b.procesosSugeridos) && b.procesosSugeridos.length > 0 && (!processes || processes.length === 0)) {
          const norm = t => String(t || "").toLowerCase().normalize("NFD").replace(/[^a-z ]/g, "");
          const filas = b.procesosSugeridos.map(pr => {
            const mt = machineTypes.find(t => {
              const a1 = norm(t.label), b1 = norm(pr.tipoMaquina);
              return a1 && b1 && (a1.includes(b1) || b1.includes(a1));
            });
            return {
              _id: uuidv4(), fakeId: true,
              name: pr.nombre || pr.tipoMaquina || "proceso",
              machineType: (!pr.externo && mt) ? mt : "",
              machines: [],
              duration: String(Math.max(1, Math.round(Number(pr.minutos) || 0))),
              external: !!pr.externo || !mt,
              price: "",
              priceSelected: "price",
            };
          });
          setProcesses(filas);
        }

                  if (b.precioReferencial && b.precioReferencial.minimo > 0) setPrice(formatInt(String(Math.round((b.precioReferencial.minimo + b.precioReferencial.maximo) / 2))));

                } catch (err) {

                  setIaError((err && err.response && err.response.data && err.response.data.error) || err.message);

                }

                setIaCargando(false);

                e.target.value = "";

              }} />

            {iaCargando && <div style={{ fontSize: 11, color: "#f39c12", marginTop: 6 }}>Leyendo el plano y armando el borrador…</div>}

            {iaError && <div style={{ fontSize: 11, color: "#e74c3c", marginTop: 6 }}>{iaError}</div>}

            {iaBorrador && !iaCargando && (

              <div style={{ marginTop: 8, fontSize: 11, color: "#cbd5e1" }}>

                <div style={{ color: "#f39c12", fontWeight: 700, marginBottom: 4 }}>📝 Borrador aplicado (confianza {iaBorrador.confianza}) — revise y corrija cada campo antes de guardar.</div>

                {Array.isArray(iaBorrador.procesosSugeridos) && iaBorrador.procesosSugeridos.length > 0 && (

                  <div style={{ marginBottom: 4 }}><b>Ruta sugerida:</b> {iaBorrador.procesosSugeridos.map(pr => pr.nombre).join(" → ")} <span style={{ color: "#7f8c8d" }}>(agréguelos abajo en PROCESOS si corresponde)</span></div>

                )}

                {iaBorrador.precioReferencial && iaBorrador.precioReferencial.maximo > 0 && (

                  <div style={{ marginBottom: 4 }}><b>Precio referencial:</b> ${Number(iaBorrador.precioReferencial.minimo).toLocaleString("es-CL")} – ${Number(iaBorrador.precioReferencial.maximo).toLocaleString("es-CL")} · {iaBorrador.precioReferencial.racional}</div>

                )}

                {iaBorrador.materialBruto && iaBorrador.materialBruto.aplica && (
                  <div style={{ marginBottom: 4 }}>
                    <b>Material en bruto (regla del taller):</b> {iaBorrador.materialBruto.geometriaTexto}
                    {iaBorrador.materialSugerido && <> · barra del catálogo: <b>{iaBorrador.materialSugerido.name}</b></>}
                    {!iaBorrador.materialSugerido && iaBorrador.materialNuevoSugerido && <> · <span style={{ color: "#f39c12" }}>no hay barra de ese Ø en el catálogo de materiales: crear "{iaBorrador.materialNuevoSugerido}"</span></>}
                    {Array.isArray(iaBorrador.materialBruto.formula) && iaBorrador.materialBruto.formula.length > 0 && (
                      <div style={{ color: "#7f8c8d", fontFamily: "monospace", fontSize: 10, marginTop: 2 }}>{iaBorrador.materialBruto.formula.join(" · ")}</div>
                    )}
                    {Array.isArray(iaBorrador.materialBruto.avisos) && iaBorrador.materialBruto.avisos.map((a, i) => <div key={i} style={{ color: "#f39c12" }}>⚠ {a}</div>)}
                    {Array.isArray(iaBorrador.materialBruto.supuestos) && iaBorrador.materialBruto.supuestos.map((s, i) => <div key={i} style={{ color: "#8a9bb5" }}>? {s}</div>)}
                  </div>
                )}
                {iaBorrador.materialBruto && !iaBorrador.materialBruto.aplica && (
                  <div style={{ color: "#8a9bb5", marginBottom: 4 }}>Material en bruto: sin cálculo ({(iaBorrador.materialBruto.avisos || []).join("; ")}). Complete Ø mayor y largo a mano.</div>
                )}

                {iaBorrador.observaciones && <div style={{ color: "#8a9bb5" }}>{iaBorrador.observaciones}</div>}

              </div>

            )}

          </div>

          <Form noValidate validated={validated} onSubmit={e => { e.preventDefault(); e.stopPropagation(); submit(); }}>

            {/* datos + material en dos columnas */}
            <div style={{ display:"flex", flexDirection:"row", gap:12, marginBottom:12 }}>
              <div style={{ flex:1, background:"#242424", border:"1px solid #2e2e2e", borderRadius:8, padding:"14px 16px" }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#e30613", textTransform:"uppercase", letterSpacing:".07em", marginBottom:10 }}>Datos generales</div>
                <div style={{ display: "flex", gap: 5, marginBottom: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 10, color: "#8a8a8a" }}>Tipo de trabajo:</span>
                  {[["ref", "🔩 REF", "#3498db"], ["fab", "🔨 FAB", "#a78bfa"], ["rep", "🔧 REP", "#f39c12"]].map(([v, l, c]) => (
                    <button key={v} type="button" onClick={() => setTipoTrabajo(v)}
                      style={{ background: tipoTrabajo === v ? "#1a2230" : "#1e1e1e", color: tipoTrabajo === v ? c : "#777", border: "1px solid " + (tipoTrabajo === v ? c : "#2e2e2e"), borderRadius: 5, padding: "3px 10px", fontSize: 10, fontWeight: tipoTrabajo === v ? 700 : 400, cursor: "pointer" }}>
                      {l}
                    </button>
                  ))}
                </div>
                <Form.Group style={{ marginBottom:8 }}>
                  <Form.Control onChange={e => setName(e.target.value)} value={name} placeholder="Nombre *" required style={{ background:"#161616", border:"1px solid #333", color:"#ddd", fontSize:12 }} />
                </Form.Group>
                <Form.Group style={{ marginBottom:8 }}>
                  <Form.Control
                    value={sku}
                    onChange={e => { setSku(e.target.value); setSkuCheck(null); }}
                    onBlur={e => verificarSKU(e.target.value)}
                    placeholder="SKU *"
                    required
                    style={{
                      background: "#161616",
                      border: `1px solid ${skuCheck?.ok === false ? "#e74c3c" : skuCheck?.ok === true ? "#2ecc71" : "#333"}`,
                      color: "#ddd",
                      fontSize: 12,
                    }}
                  />
                  {skuCheck === "checking" && (
                    <div style={{ fontSize:11, color: "#cbd5e1", marginTop:4 }}>⏳ Verificando SKU…</div>
                  )}
                  {skuCheck?.ok === true && (
                    <div style={{ fontSize:11, marginTop:4, padding:"4px 8px", borderRadius:4, background:"#0d2a15", color:"#2ecc71", border:"1px solid #0d4020" }}>
                      ✓ SKU disponible — no existe en el catálogo
                    </div>
                  )}
                  {skuCheck?.ok === false && (
                    <div style={{ fontSize:11, marginTop:4, padding:"6px 10px", borderRadius:4, background:"#2d0000", color:"#e74c3c", border:"1px solid #4a0000" }}>
                      ⚠ SKU ya existe en el catálogo → <strong>{skuCheck.existing?.name}</strong>
                      <div style={{ marginTop:3, fontSize:10, color:"#a77" }}>
                        Revisa si es la misma pieza antes de duplicar. Si el código es diferente, corrígelo antes de guardar.
                      </div>
                    </div>
                  )}
                </Form.Group>
                <Form.Group style={{ marginBottom:8 }}>
                  <Form.Control onChange={e => setDetails(e.target.value)} value={details} placeholder="Detalles" style={{ background:"#161616", border:"1px solid #333", color:"#ddd", fontSize:12 }} />
                </Form.Group>
                <Form.Group style={{ marginBottom:8 }}>
                  <IntegerInput onChange={e => setMinStock(e.target.value)} value={minStock} placeholder="Stock mínimo" style={{ background:"#161616", border:"1px solid #333", color:"#ddd", fontSize:12, width:"100%" }} />
                </Form.Group>
                {drive && (
                  <FileInput url={blueprint?.url} onChange={handleBlueprintUpload} onRemove={handleBlueprintRemove} accept=".pdf">
                    <i className="bi bi-file-pdf align-self-center" />
                    <div className="fw-bold">Plano</div>
                  </FileInput>
                )}
              </div>
              <div style={{ flex:1, background:"#242424", border:"1px solid #2e2e2e", borderRadius:8, padding:"14px 16px" }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#e30613", textTransform:"uppercase", letterSpacing:".07em", marginBottom:10 }}>Material</div>
                <Form.Group style={{ marginBottom:8 }}>
                  <Form.Control onChange={e => setMaterial(e.target.value)} value={material} placeholder="Material" style={{ background:"#161616", border:"1px solid #333", color:"#ddd", fontSize:12 }} />
                </Form.Group>
                <Form.Group>
                  <Form.Control onChange={e => setGeometry(e.target.value)} value={geometry} placeholder="Geometría" style={{ background:"#161616", border:"1px solid #333", color:"#ddd", fontSize:12 }} />
                </Form.Group>
              </div>
            </div>

            <div style={{ background: "var(--bini-surface)", border: "1px solid var(--bini-border)", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--bini-primary)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>Ficha comercial y producción</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
                <Form.Group><Form.Label>Precio cliente general</Form.Label><PriceInput value={priceClient} onChange={setPriceClient} /></Form.Group>
                <Form.Group><Form.Label>Material de inventario</Form.Label><Form.Select value={materialRef} onChange={e => setMaterialRef(e.target.value)}><option value="">Sin material vinculado</option>{rawMaterials.map(m => <option key={m._id} value={m._id}>{m.name}{m.materialType ? ` · ${m.materialType}` : ""}</option>)}</Form.Select></Form.Group>
                <Form.Group><Form.Label>Consumo por unidad</Form.Label><Form.Control type="number" min="0.001" step="0.001" value={consumoPorUnidad} onChange={e => setConsumoPorUnidad(e.target.value)} disabled={!materialRef} /></Form.Group>
                <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}><Form.Check type="checkbox" label="Requiere dossier" checked={requiereDosier} onChange={e => setRequiereDosier(e.target.checked)} /><Form.Check type="checkbox" label="Pieza importada" checked={esImportada} onChange={e => setEsImportada(e.target.checked)} /></div>
              </div>
              <div style={{ marginTop: 12, borderTop: "1px solid var(--bini-border)", paddingTop: 10 }}><strong>BOM · sólo lectura ({components.length})</strong>{components.length === 0 ? <div style={{ color: "var(--bini-text-muted)", fontSize: 12 }}>Sin componentes. El BOM se administra desde el detalle del catálogo.</div> : <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>{components.map((c, i) => <li key={c._id || i}>{c.piece?.sku ? `${c.piece.sku} · ` : ""}{c.piece?.name || "Componente"} × {c.quantity || 1}{c.notes ? ` — ${c.notes}` : ""}</li>)}</ul>}</div>
            </div>

            {/* Control dimensional F1 — plano maestro, cotas críticas y política.
                Sólo en edición: las cotas se cuelgan de una pieza que ya existe, y en
                una pieza nueva no habría dónde guardarlas hasta el primer save. */}
            {_id && <PlanoYCotas pieceId={_id} precio={price} />}

            {/* procesos */}
            <div style={{ background:"#242424", border:"1px solid #2e2e2e", borderRadius:8, padding:"14px 16px", marginBottom:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#e30613", textTransform:"uppercase", letterSpacing:".07em" }}>Procesos</div>
                <span style={{ fontSize:10, color: "#aaa", fontStyle:"italic" }}>
                  Opcional · si ya tenés el precio final, podés dejarlo vacío
                </span>
              </div>
              {(!processes || processes.length === 0) ? (
                <button
                  type="button"
                  onClick={() => setProcesses([getNewProcess()])}
                  style={{
                    width:"100%", background:"transparent", color: "#cbd5e1",
                    border:"1px dashed #444", borderRadius:5,
                    padding:"10px", fontSize:12, cursor:"pointer",
                  }}
                >
                  + Agregar primer proceso (calcular precio con materiales/máquinas)
                </button>
              ) : (
                <div style={{ display:"grid", gap:"16px", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))" }}>
                  {processes.map((v, i) => (
                    <Item key={i} index={i} item={v} setItems={setProcesses} machineTypes={machineTypes} />
                  ))}
                </div>
              )}
            </div>

            {/* precio */}
            <div style={{ background:"#242424", border:"1px solid #2e2e2e", borderRadius:8, padding:"14px 16px", marginBottom:12 }}>
              <div style={{ fontSize:10, fontWeight:700, color:"#e30613", textTransform:"uppercase", letterSpacing:".07em", marginBottom:12 }}>Precio</div>
              <PiecePrice
                piece={{ processes, constants, materialPrice, price }}
                onProcessPriceSelectedChanged={handleProcessPriceSelectedChanged}
                onMaterialPriceChanged={setMaterialPrice}
                onProfitChanged={handleProfitChanged}
                onPriceChanged={setPrice}
                updateConstants={handleConstantsUpdate}
                editable
              />
            </div>

            {error && (
              <div style={{ color:"#e74c3c", fontSize:12, textAlign:"center", marginBottom:10 }}>{error}</div>
            )}
            {draftBtnMsg && (
              <div style={{ fontSize: 11, color: draftBtnMsg.startsWith("✔") ? "#2ecc71" : "#e74c3c", textAlign: "right", marginBottom: 6 }}>{draftBtnMsg}</div>
            )}
            <div style={{ display:"flex", flexDirection:"row", gap:8, justifyContent:"flex-end" }}>
              {!_id && (
                <button type="button" onClick={guardarBorradorAhora} style={{
                  background:"#1a2a3a", color:"#5dade2", border:"1px solid #2a4a6a",
                  borderRadius:6, padding:"7px 14px", fontSize:12, cursor:"pointer", fontWeight:700,
                }}>💾 Guardar borrador</button>
              )}
              <button type="button" onClick={cancel} style={{
                background:"transparent", color: "#cbd5e1", border:"1px solid #444",
                borderRadius:6, padding:"7px 14px", fontSize:12, cursor:"pointer",
              }}>Cancelar</button>
              <button type="button" onClick={submit} style={{
                background:"#e30613", color:"#fff", border:"none",
                borderRadius:6, padding:"7px 18px", fontSize:12,
                fontWeight:700, cursor:"pointer",
              }}>{_id ? "Guardar cambios" : "Crear pieza"}</button>
            </div>
            <LoadingOverlay ref={loadingOverlayRef} />
          </Form>
        </div>
        <Form.Control onChange={handleBlueprintUpload} type="file" accept=".pdf" className="d-none" ref={blueprintRef} />
      </WaitAsync>
    </CheckBack>
  );
};

const Item = ({ index, item, setItems, machineTypes }) => {
  const { machineType, duration, name, machines, external, price } = item;

  const set = (k, i) => v => {
    setItems(p => {
      const n = [...p];
      n[i] = { ...n[i], [k]: v };
      return n;
    });
  };

  const setMachineType = set("machineType", index);
  const setDuration = set("duration", index);
  const setName = set("name", index);
  const setMachines = set("machines", index);
  const setExternal = set("external", index);
  const setPrice = set("price", index);

  const remove = () => {
    setItems(p => {
      const n = [...p];
      n.splice(index, 1);
      return n;
    });
  };

  const [machineOptions, setMachineOptions] = useState([]);
  const [machineOptionsLoading, setMachineOptionsLoading] = useState(false);

  const getMachineOptions = async mt => {
    setMachines([]);
    setMachineOptionsLoading(true);
    try {
      const machinesData = (await backend.get(`machine/type/${mt._id}`)).data;
      setMachineOptions(
        machinesData.map(v => ({
          ...v,
          value: v._id,
          label: v.name,
        }))
      );
    } catch (err) {
      console.log(err);
    }
    setMachineOptionsLoading(false);
  };

  const getNewProcess = () => ({
    _id: uuidv4(),
    fakeId: true,
    machineType: "",
    machines: [],
    duration: "",
    name: "",
    external: false,
    price: "",
    priceSelected: "price",
  });

  const setProcessLeft = () => {
    setItems(p => {
      const n = [...p];
      n.splice(index, 0, getNewProcess());
      return n;
    });
  };

  const setProcessRight = () => {
    setItems(p => {
      const n = [...p];
      n.splice(index + 1, 0, getNewProcess());
      return n;
    });
  };

  return (
    <Card bg="dark">
      <Card.Body>
        <div className="m2-col flex-fill">
          <div className="m2-row">
            <div
              className="text-end fw-bold fst-italic text-muted user-select-none"
              style={{
                fontSize: "24px",
              }}
            >
              {`#${index + 1}`}
            </div>
            <ButtonGroupFilter
              options={{
                [false]: "Interno",
                [true]: "Externo",
              }}
              tabState={[external, setExternal]}
              className="flex-fill"
              parseKey={v => v === "true"}
            />
            <Button
              variant="secondary flex-center ms-2"
              onClick={remove}
              style={{ width: 40, height: 40 }}
            >
              <i className="bi bi-x-lg" />
            </Button>
          </div>
          <Form.Group className="">
            <Form.Control
              onChange={e => setName(e.target.value)}
              value={name}
              placeholder="Nombre"
              required
            />
          </Form.Group>

          {external ? (
            <>
              <div className="m3-row flex-fill">
                <Form.Group className="flex-fill" style={{ position: "relative" }}>
                  <Form.Control
                    onChange={e => setDuration(formatInt(e.target.value))}
                    value={duration}
                    placeholder="Tiempo (días)"
                    style={{ paddingRight: "42px" }}
                    required
                  />
                  <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "#8a8a8a", pointerEvents: "none" }}>días</span>
                </Form.Group>
                <PriceInput
                  className=""
                  onChange={v => setPrice(v)}
                  value={price}
                  required
                />
              </div>
            </>
          ) : (
            <>
              <div className="m2-row">
                <Form.Group style={{ position: "relative" }}>
                  <Form.Control
                    onChange={e => setDuration(formatInt(e.target.value))}
                    value={duration}
                    placeholder="Duración (min)"
                    style={{ width: "120px", paddingRight: "38px" }}
                    required
                  />
                  <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "#8a8a8a", pointerEvents: "none" }}>min</span>
                </Form.Group>
                <Select
                  className="flex-fill"
                  styles={{
                    option: styles => ({ ...styles, color: "black" }),

                    input: styles => ({ ...styles, flexDirection: "row" }),
                  }}
                  onChange={v => {
                    setMachineType(v);
                    getMachineOptions(v);
                  }}
                  value={machineType}
                  options={machineTypes}
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <div className="fw-bold">Máquinas Capaces</div>
                <Select
                  styles={{
                    option: styles => ({ ...styles, color: "black" }),
                    control: styles => ({
                      ...styles,
                      flexDirection: "row",
                      minWidth: "200px",
                    }),
                    input: styles => ({ ...styles, flexDirection: "row" }),
                    multiValue: styles => ({ ...styles, color: "black" }),
                  }}
                  isMulti
                  value={machines}
                  onChange={setMachines}
                  isLoading={machineOptionsLoading}
                  options={machineOptions}
                  classNamePrefix="react-select"
                  isDisabled={!machineType}
                />
              </div>
            </>
          )}
          <div
            style={{
              justifyContent: "space-between",
            }}
            className="d-flex justify-content-between flex-row"
          >
            <Button
              style={{ width: 40, height: 40 }}
              variant="secondary"
              className="flex-center position-relative"
              onClick={setProcessLeft}
            >
              <i className="bi bi-plus-lg" />
              <i
                className="bi bi-arrow-left-short position-absolute"
                style={{ top: 2, left: 2 }}
              />
            </Button>
            <Button
              style={{ width: "40px" }}
              variant="secondary"
              className="flex-center position-relative"
              onClick={setProcessRight}
            >
              <i className="bi bi-plus-lg" />
              <i
                className="bi bi-arrow-right-short position-absolute"
                style={{ top: 2, right: 2 }}
              />
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default PieceForm;
